import { Component, computed, inject, input, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Select, SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { NgIcon } from '@ng-icons/core';

import { InjectAttributeDirective } from '@directives/inject-attribute.directive';

import { CurrencyEmoji } from '@cxc/core/models';

import { AnalyticsLabelValuePipe } from '@cxc/ui-kit/pipes';

import { PendoService, RouterState } from '@cxc/core/services';
import { CurrencyState } from '@services/state/currency-state/currency.state';
import { OperationsState } from '@services/state/operation-state/operation.state';

/**
 * One name for every screen (LEN-2466), with the screen as a property: the
 * question it answers - do Colombian users stay in COP or go back to MXN? -
 * is about the switch, wherever it happens. Listed in docs/pendo-events.md.
 */
const CURRENCY_CHANGED_EVENT = 'portfolio/currency-changed';

@Component({
  selector: 'currency-selector',
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    TagModule,
    TooltipModule,
    NgIcon,
    InjectAttributeDirective,
    AnalyticsLabelValuePipe,
  ],
  template: `
    <!-- Nothing to offer, nothing offered (LEN-2125): while the catalogue is
         loading — or if it comes back empty — an empty dropdown said
         "No results found" with the FX disclosure icon next to it, promising
         to explain a conversion that can't be chosen. The block appears when
         the options do. -->
    @if (currencyState.flagsAndCurrency().length) {
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-1">
          <p>Currency</p>
          <ng-icon
            [pTooltip]="tooltip()"
            tooltipPosition="right"
            name="phosphorInfo" />
        </div>
        <div class="flex items-center gap-2">
          <p-select
            [options]="currencyState.flagsAndCurrency()"
            [ngModel]="currencyState.currencySelected()"
            (ngModelChange)="onCurrencyPicked($event)"
            optionDisabled="disabled"
            [injectAttribute]="[
              {
                CSSPath: '.p-select-label, .p-select-dropdown',
                attribute: 'analytics-label',
                value:
                  {
                    element: 'toolbar/select',
                    action: 'currency',
                  } | analyticsLabelValue,
              },
            ]">
            <ng-template let-selected pTemplate="selectedItem">
              @if (selected) {
                <span> {{ selected.emojiFlag }} {{ selected.currency }} </span>
              }
            </ng-template>
            <ng-template let-item pTemplate="item">
              <!-- PrimeNG puts p-disabled + pointer-events:none on the <li>; the
                   tooltip host has to opt back into pointer events or mouseenter
                   never fires (LEN-2135). The reason is also in the label so it
                   stays visible without hover.
                   That opt-in also lets a click through, and PrimeNG 18 blocks
                   disabled options with that CSS only - the <li> handler never
                   checks disabled - so the click stops here (LEN-2551). -->
              <span
                class="flex items-center gap-1"
                style="pointer-events: auto"
                data-testid="currency-option-content"
                (click)="onOptionContentClick($event, item)"
                [pTooltip]="item.disabledReason"
                [tooltipDisabled]="!item.disabled"
                tooltipPosition="right">
                {{ item.emojiFlag }} {{ item.currency }}
                @if (item.disabled && item.disabledReason) {
                  <span class="text-surface-500"
                    >— {{ item.disabledReason }}</span
                  >
                }
              </span>
            </ng-template>
          </p-select>
          <!-- LEN-1814 Escenario 4: only on a facility-scoped page (Insights,
               Concentrations, Cash-flow) - nativeCurrency() is undefined on
               Overview, which has no single operation to be native to. States
               which of the two readings (the facility's own currency vs. a
               re-expressed one) is on screen, mirroring financials-mfe's
               Reporting currency badge (LEN-1812). Rendered next to the
               select, not below it (review suggestion 1, MR !1246). -->
          @if (currencyState.nativeCurrency()) {
            <p-tag
              [severity]="currencyState.isNative() ? 'secondary' : 'info'"
              [value]="currencyState.isNative() ? 'Native' : 'Converted'" />
          }
        </div>
      </div>
    }
  `,
})
export class CurrencySelector {
  tooltip = input('');

  currencyState = inject(CurrencyState);
  routerState = inject(RouterState);
  private pendoService = inject(PendoService);
  private operationsState = inject(OperationsState);

  private select = viewChild(Select);

  analyticsLabel = computed(
    () => `${this.routerState.analyticsLabelRoot()}/toolbar/select/currency`
  );

  /**
   * Stops a click on a disabled option before it reaches PrimeNG's <li>
   * handler (LEN-2551). A method rather than an inline
   * `item.disabled && $event.stopPropagation()`: that expression evaluates to
   * `false` for an option with `disabled: false`, and Angular calls
   * `preventDefault()` on any event whose handler returns `false`.
   */
  onOptionContentClick(event: MouseEvent, option: CurrencyEmoji): void {
    if (option.disabled) {
      event.stopPropagation();
    }
  }

  /**
   * The user's pick, and only the user's: `ngModelChange` comes from the view,
   * so `setDefaultCurrency` and the no-rate fallback (`markUnavailable`), which
   * write the signal directly, are not counted as switches nobody made.
   *
   * The same-currency guard is load-bearing, not defensive: PrimeNG's
   * `Select.writeValue` calls `onModelChange(value)` whenever
   * `allowModelChange()` holds (`autoDisplayFirst`, no placeholder, empty
   * internal model). So the app's first programmatic write - e.g.
   * `setDefaultCurrency` after a deep link - comes back as an
   * `ngModelChange` carrying the value just written. Without the guard that
   * echo would be tracked as a switch.
   *
   * The disabled guard is the second line behind the template's click stop
   * (LEN-2551, LEN-2460 scenario 8): PrimeNG's `onOptionSelect` never checks
   * disabled, and focusOnHover lets the mouse focus a disabled option that
   * Enter/Tab then select. focusOnHover stays on: PrimeNG 18 has no `:hover`
   * style for options, so the `.p-focus` it sets is the only hover highlight
   * enabled options get. By then PrimeNG has already moved its own model to
   * the disabled option, and `[ngModel]` won't rewrite a signal that didn't
   * change, so the previous value is written back by hand - in a
   * microtask, because `Select.updateModel` sets `modelValue` again right
   * after emitting. The internal model is no longer empty, so that write is
   * not echoed back.
   */
  onCurrencyPicked(next: CurrencyEmoji) {
    const previous = this.currencyState.currencySelected();

    if (next?.disabled) {
      queueMicrotask(() => this.select()?.writeValue(previous));
      return;
    }

    this.currencyState.currencySelected.set(next);

    if (previous?.currency === next.currency) {
      return;
    }

    const screen = this.routerState.analyticsLabelRoot();
    this.pendoService.trackEvent(
      CURRENCY_CHANGED_EVENT,
      {
        from: previous?.currency ?? '',
        to: next.currency,
        screen,
        // Overview has no single operation; a stale id from the last facility
        // visited would attribute the switch to the wrong one.
        facility_id: screen.startsWith('portfolio/facility/')
          ? this.operationsState.activeId()
          : '',
      },
      false
    );
  }
}
